import { WhenFilter } from '../models/FSMenu';

/**
 * Parses a weekday abbreviation to a number (0=Monday, 6=Sunday)
 * Supports Swedish and English abbreviations
 */
function parseAsWeekday(part: string): number | null {
  if (part.length < 3) {
    return null;
  }

  const s = part.substring(0, 3).toLowerCase();

  const weekdays_sv = ['mån', 'tis', 'ons', 'tor', 'fre', 'lör', 'sön'];
  const weekdays_en = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  const svIndex = weekdays_sv.indexOf(s);
  if (svIndex !== -1) {
    return svIndex;
  }

  const enIndex = weekdays_en.indexOf(s);
  if (enIndex !== -1) {
    return enIndex;
  }

  return null;
}

/**
 * Parses a time string to hours and minutes
 */
function parseAsTime(part: string): { h: number; m: number } | null {
  if (part.includes(':')) {
    const hm = part.split(':');
    if (hm.length === 2) {
      try {
        const h = parseInt(hm[0], 10);
        const m = parseInt(hm[1], 10);
        return { h, m };
      } catch (e) {
        return null;
      }
    }
  } else if (/^\d+$/.test(part)) {
    // Check if string contains only digits
    try {
      const h = parseInt(part, 10);
      return { h, m: 0 };
    } catch (e) {
      return null;
    }
  }

  return null;
}

/**
 * Parses a date string to YYYYMMDD format
 * Supports formats:
 * - 6/3 (day/month, uses current year)
 * - 2021/6/3 (year/month/day)
 * - 20210603 (YYYYMMDD)
 * - 210603 (YYMMDD)
 * - 2021-06-03 (YYYY-MM-DD)
 * - 21-06-03 (YY-MM-DD)
 */
function parseAsDate(part: string): string | null {
  if (!part || part === 'no-date') {
    return null;
  }

  const slashCount = (part.match(/\//g) || []).length;

  try {
    // Format: day/month (uses current year)
    if (slashCount === 1) {
      const match = part.match(/^(\d{1,2})\/(\d{1,2})$/);
      if (match) {
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10);
        const year = new Date().getFullYear();
        return `${year}${month.toString().padStart(2, '0')}${day.toString().padStart(2, '0')}`;
      }
    }
    // Format: year/month/day
    else if (slashCount === 2) {
      const match = part.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
      if (match) {
        const year = match[1];
        const month = parseInt(match[2], 10);
        const day = parseInt(match[3], 10);
        return `${year}${month.toString().padStart(2, '0')}${day.toString().padStart(2, '0')}`;
      }
    }
    // Format: YYYY-MM-DD or YY-MM-DD
    else if (part.includes('-')) {
      const match = part.match(/^(\d{2,4})-(\d{1,2})-(\d{1,2})$/);
      if (match) {
        let year = match[1];
        const month = parseInt(match[2], 10);
        const day = parseInt(match[3], 10);

        // Handle 2-digit year
        if (year.length === 2) {
          const currentYear = new Date().getFullYear();
          const century = Math.floor(currentYear / 100) * 100;
          year = (century + parseInt(year, 10)).toString();
        }

        return `${year}${month.toString().padStart(2, '0')}${day.toString().padStart(2, '0')}`;
      }
    }
    // Format: YYYYMMDD or YYMMDD
    else {
      if (part.length === 8) {
        // YYYYMMDD
        const match = part.match(/^(\d{4})(\d{2})(\d{2})$/);
        if (match) {
          return part;
        }
      } else if (part.length === 6) {
        // YYMMDD
        const match = part.match(/^(\d{2})(\d{2})(\d{2})$/);
        if (match) {
          const currentYear = new Date().getFullYear();
          const century = Math.floor(currentYear / 100) * 100;
          const year = century + parseInt(match[1], 10);
          return `${year}${match[2]}${match[3]}`;
        }
      }
    }
  } catch (e) {
    // Silently fail
  }

  return null;
}

/**
 * Parses a single when expression part
 */
function parseSingleWhenPart(when: string): WhenFilter | null {
  const parts = when.replace(/-/g, ' ').split(/\s+/).filter(p => p.length > 0);

  let btime: string | null = null;
  let etime: string | null = null;
  let bdate: string | null = null;
  let edate: string | null = null;
  let bwd: number | null = null;
  let ewd: number | null = null;

  for (const part of parts) {
    // Try parsing as date
    const d = parseAsDate(part);
    if (d) {
      if (!bdate) {
        bdate = d;
      } else {
        edate = d;
      }
      continue;
    }

    // Try parsing as weekday
    const wd = parseAsWeekday(part);
    if (wd !== null) {
      if (bwd === null) {
        bwd = wd;
      } else {
        ewd = wd;
      }
      continue;
    }

    // Try parsing as time
    const time = parseAsTime(part);
    if (time !== null) {
      let h = time.h;
      const m = time.m;

      // Hours < 6 are considered late night (add 24)
      if (h < 6) {
        h += 24;
      }

      const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;

      if (btime === null) {
        btime = timeStr;
      } else {
        etime = timeStr;
      }
      continue;
    }
  }

  // If nothing was parsed, return null
  if (btime === null && etime === null && bdate === null && edate === null && bwd === null && ewd === null) {
    return null;
  }

  const result: WhenFilter = {};

  if (btime) {
    result.bt30 = btime;
  }
  if (etime) {
    result.et30 = etime;
  }
  if (bdate) {
    result.bd = bdate;
  }
  if (edate) {
    result.ed = edate;
  }

  // Handle weekday range
  if (bwd !== null) {
    if (ewd === null) {
      ewd = bwd;
    }

    // Handle wrap-around (e.g., fri-mon)
    let adjustedEwd = ewd;
    if (ewd < bwd) {
      adjustedEwd += 7;
    }

    const wds: number[] = [];
    for (let i = bwd; i <= adjustedEwd; i++) {
      wds.push(i % 7);
    }
    result.wds = wds;
  }

  return result;
}

/**
 * Parses a string representation of a WhenFilter into a WhenFilter object.
 *
 * @param whenStr - The string to parse (e.g., "mon 10-14" or "mon 10-14, tue 09-17")
 * @returns A WhenFilter object if parsing succeeds, null otherwise
 */
export function parseWhenFilterString(whenStr: string): WhenFilter | null {
  if (!whenStr || typeof whenStr !== 'string') {
    return null;
  }

  // Split by comma for multiple when expressions
  const whens = whenStr.split(',').map(w => w.trim());

  // Parse the first expression (for now, we only return the first one)
  // In the future, this could return an array of WhenFilters
  for (const when of whens) {
    const result = parseSingleWhenPart(when);
    if (result) {
      return result;
    }
  }

  return null;
}
